import { useCallback, useEffect, useRef } from 'react';
import cn from 'classnames';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useLocale } from '@hooks/useLocale';
import { usePdp } from '@context/PdpContext';
import { usePdpSelectionFromQuery } from '_components/ColourPicker/usePdpSelectionFromQuery';
import { MecProduct, PriceType } from '@adapters/bigcommerce/types/product';
import { trackProductEvent, GA4_EVENT_NAMES } from '@utils/gtm/ga4';
import { getGenderFromString } from '@utils/gtm/ga4/utils';
import { albyTrackLoadPDP } from '@utils/alby';
import { FEATURE_FLAG_ALBY_CHATBOT } from '@config/feature-flag';

import styles from './ProductColourSelector.module.scss';

const { VIEW_ITEM } = GA4_EVENT_NAMES;

export const COLOUR_GROUPS = [
  { label: 'product.regular.colours', priceType: PriceType.Regular },
  { label: 'product.sale.colours', priceType: PriceType.Sale },
  { label: 'product.clearance.colours', priceType: PriceType.Clearance },
];

interface ProductPathInterface {
  path: string;
  product: MecProduct;
}

const ProductColourSelector = ({ path, product }: ProductPathInterface) => {
  const {
    colours,
    selectedColour,
    selectedColourId,
    setSelectedColourId,
    isGiftCard,
    promoBadge,
    price,
    hasComparison,
    hasFranchiseComparison,
    hasMemberPriceDifference,
    selectedVariant,
  } = usePdp();
  const { t } = useTranslation('pdp');
  const locale = useLocale();
  const firstFiredFlags = useRef<Array<'tracking'>>([]);

  usePdpSelectionFromQuery({ path, product });

  const updatedProduct = useCallback(() => {
    return {
      ...product,
      gender: getGenderFromString(product?.name),
      selectedColour: selectedColour?.name,
      promoBadge: promoBadge ?? undefined,
      selectedPrice: price
        ? {
            basePrice: price.basePrice,
            price: price.price,
          }
        : undefined,
      hasComparison: hasComparison,
      hasFranchiseComparison: hasFranchiseComparison,
      hasMemberPriceDifference,
    };
  }, [
    price,
    product,
    promoBadge,
    selectedColour?.name,
    hasComparison,
    hasFranchiseComparison,
    hasMemberPriceDifference,
    selectedVariant,
  ]);

  useEffect(() => {
    if (selectedColour?.name && !firstFiredFlags.current.includes('tracking')) {
      const updatedProductData = updatedProduct();
      trackProductEvent({
        eventName: VIEW_ITEM,
        products: [updatedProductData],
      });
      if (
        updatedProductData?.code &&
        locale === 'en' &&
        FEATURE_FLAG_ALBY_CHATBOT
      ) {
        albyTrackLoadPDP(updatedProductData?.code);
      }
      firstFiredFlags.current.push('tracking');
    }
  }, [selectedColour, updatedProduct, locale]);

  if (
    !colours?.length ||
    (colours.length === 1 && colours[0].name === 'NO_COLOUR')
  )
    return null;

  if (colours.length === 1) {
    return (
      <p className={styles.singleColourName}>
        {t('product.colour')}{' '}
        <strong data-testid="single-colour-name">{colours[0].name}</strong>
      </p>
    );
  }

  const swatchGroups = COLOUR_GROUPS.flatMap((colourGroup) => {
    const coloursInGroup = colours.filter(
      (colour) => colour.price?.priceType === colourGroup.priceType
    );

    if (!coloursInGroup.length) return [];

    return (
      <div
        className={styles.container}
        key={`colour-group-${colourGroup.label}`}
      >
        <p
          className={cn(styles.swatchGroupTitle, {
            [styles.swatchGroupReduced]: [
              PriceType.Sale,
              PriceType.Clearance,
            ].includes(colourGroup.priceType),
          })}
        >
          {t(colourGroup.label)}
        </p>
        <ul className={styles.swatchList}>
          {coloursInGroup?.map((colour) => (
            <li key={`colour-${colour.id}`}>
              <button
                className={cn(styles.swatchButton, {
                  [styles.swatchButtonSelected]: colour.id === selectedColourId,
                  [styles.swatchButtonDisabled]:
                    colour.notAvailableForSelectedSize,
                  [styles.swatchButtonColourNotAvailable]: !colour.swatchImage,
                })}
                type="button"
                onClick={() => {
                  if (setSelectedColourId) setSelectedColourId(colour.id);
                }}
              >
                <span className={styles.screenReader}>{colour.name}</span>
                {colour.swatchImage ? (
                  <Image
                    width={24}
                    height={24}
                    src={colour.swatchImage}
                    className={cn(styles.swatchButtonImage, {
                      [styles.swatchButtonImageDisabled]:
                        colour.notAvailableForSelectedSize,
                    })}
                    alt=""
                    quality={60}
                    style={{
                      maxWidth: '100%',
                      height: 'auto',
                    }}
                  />
                ) : (
                  <span
                    className={cn(styles.swatchButtonHex, {
                      [styles.swatchButtonHexDisabled]:
                        colour.notAvailableForSelectedSize,
                    })}
                    style={{
                      backgroundColor: 'var(--color-white)',
                    }}
                  />
                )}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  });

  return (
    <div>
      <p className={styles.colourName}>
        {isGiftCard ? t('product.giftcard.card.design') : t('product.colour')}{' '}
        <strong data-testid="selected-colour-name">
          {selectedColour?.name}
        </strong>
      </p>
      {swatchGroups}
    </div>
  );
};

export default ProductColourSelector;
